import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TenantUser {
  user_id: string;
  email: string;
  role: 'tenant_admin' | 'user';
  profile: {
    first_name: string | null;
    last_name: string | null;
  };
}

interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  role: 'tenant_admin' | 'user';
  tenantId: string;
}

export function useTenantUsers(tenantId: string | null, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  // Fetch users for a specific tenant with their email from auth.users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['tenant-users', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      // Get user_roles with profiles including email
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          profile:user_id (
            first_name,
            last_name,
            email
          )
        `)
        .eq('tenant_id', tenantId)
        .in('role', ['tenant_admin', 'user'])
        .order('role', { ascending: false }); // tenant_admin first

      if (rolesError) throw rolesError;

      // Transform the data and remove duplicates
      const usersMap = new Map<string, TenantUser>();

      userRoles.forEach((ur: any) => {
        // Only add if not already in map, or if this role is "higher" than existing
        if (!usersMap.has(ur.user_id) || ur.role === 'tenant_admin') {
          usersMap.set(ur.user_id, {
            user_id: ur.user_id,
            email: ur.profile?.email || '',
            role: ur.role,
            profile: ur.profile || { first_name: null, last_name: null }
          });
        }
      });

      return Array.from(usersMap.values());
    },
    enabled: options?.enabled !== false && !!tenantId,
  });

  // Update user role
  const updateUserRole = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: 'user' | 'tenant_admin' }) => {
      if (!tenantId) throw new Error('Tenant ID is required');

      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      toast.success('User role updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update user role');
    },
  });

  // Create new user via Edge Function
  const createUser = useMutation({
    mutationFn: async (data: CreateUserData) => {
      const { data: result, error } = await supabase.functions.invoke('create-tenant-user', {
        body: data
      });

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-user-counts'] });
      toast.success('User invitation sent successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create user');
    },
  });

  // Remove user from tenant
  const removeUser = useMutation({
    mutationFn: async (userId: string) => {
      if (!tenantId) throw new Error('Tenant ID is required');

      // First, get the user's email from profiles to clean up invitations
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', userId)
        .single();

      // Delete pending invitations for this user/tenant
      if (profile?.email) {
        await supabase
          .from('user_invitations')
          .delete()
          .eq('email', profile.email)
          .eq('tenant_id', tenantId)
          .is('invitation_accepted_at', null);
      }

      // Delete the user_role entry
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (roleError) throw roleError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-user-counts'] });
      toast.success('User removed from tenant successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove user');
    },
  });

  // Bulk update user roles
  const bulkUpdateRoles = useMutation({
    mutationFn: async ({ userIds, newRole }: { userIds: string[]; newRole: 'user' | 'tenant_admin' }) => {
      if (!tenantId) throw new Error('Tenant ID is required');

      const promises = userIds.map(userId =>
        supabase
          .from('user_roles')
          .update({ role: newRole })
          .eq('user_id', userId)
          .eq('tenant_id', tenantId)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} user(s)`);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      toast.success(`Updated ${variables.userIds.length} user(s) successfully`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update users');
    },
  });

  // Bulk remove users
  const bulkRemoveUsers = useMutation({
    mutationFn: async (userIds: string[]) => {
      if (!tenantId) throw new Error('Tenant ID is required');

      // Get emails for all users being removed
      const { data: profiles } = await supabase
        .from('profiles')
        .select('email')
        .in('id', userIds);

      // Delete pending invitations for these users/tenant
      if (profiles && profiles.length > 0) {
        const emails = profiles.map(p => p.email).filter(Boolean);
        if (emails.length > 0) {
          await supabase
            .from('user_invitations')
            .delete()
            .in('email', emails)
            .eq('tenant_id', tenantId)
            .is('invitation_accepted_at', null);
        }
      }

      // Delete user_roles entries
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('tenant_id', tenantId)
        .in('user_id', userIds);

      if (error) throw error;
    },
    onSuccess: (_, userIds) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-user-counts'] });
      toast.success(`Removed ${userIds.length} user(s) successfully`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove users');
    },
  });

  return {
    users,
    isLoading,
    updateUserRole,
    createUser,
    removeUser,
    bulkUpdateRoles,
    bulkRemoveUsers,
  };
}
